"use client"

import { saveAdvertisementForUser, getUser } from '@/app/actions';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { RecordModel } from 'pocketbase';
import { toast } from 'react-toastify';

function FavouriteButton({ adId, size = 'small' }: { adId: string, size?: 'small' | 'large' }) {
  const queryClient = useQueryClient();

  const { data: user, isLoading: userLoading } = useQuery({
    queryKey: ['user'],
    queryFn: () => getUser(),
    staleTime: Infinity, // Cache the user data indefinitely while on the page
  });

  const saveMutation = useMutation({
    mutationFn: saveAdvertisementForUser,
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey: ['user'] });
      const previousUser = queryClient.getQueryData(['user']);
      queryClient.setQueryData(['user'], (old: RecordModel | null) => {
        if (!old) return old;
        const savedAds = old.savedAdvertisements || [];
        return {
          ...old,
          savedAdvertisements: savedAds.includes(adId)
            ? savedAds.filter((id: string) => id !== adId)
            : [...savedAds, adId],
        };
      });
      return { previousUser };
    },
    onError: (error, _, context: any) => {
      queryClient.setQueryData(['user'], context.previousUser);
      toast.error('Failed to update favourite. Please try again.');
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey: ['user'] });
    },
  });

  const isFavourite = (user as RecordModel | null)?.savedAdvertisements?.includes(adId) || false;

  const handleClick = (e: { preventDefault: () => void; stopPropagation: () => void; }) => {
    e.preventDefault();
    e.stopPropagation();
    if (!user) {
      document.getElementById("sign-up-modal")?.click();
    } else {
      saveMutation.mutate(adId);
    }
  };

  if (userLoading) {
    return (
      <button
        className={`btn ${size === 'small' ? 'btn-circle btn-sm' : 'btn-ghost'} border-none hover:bg-white/50 rounded-full bg-white text-secondary flex items-center justify-center`}
        disabled={true}
      >
        {size === 'small' ? (
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill={isFavourite ? "currentColor" : "none"}
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
        ) : (
          <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill={isFavourite ? "currentColor" : "none"}
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          <span>Save</span>
          </>
        )}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`btn ${size === 'small' ? 'btn-circle btn-sm ' : 'btn-ghost'} border-none hover:bg-secondary/20 rounded-full bg-white text-secondary flex items-center justify-center`}
    >
      {size === 'small' ? (
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-5 w-5"
          fill={isFavourite ? "currentColor" : "none"}
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
          />
        </svg>
      ) : (
        <>
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            fill={isFavourite ? "currentColor" : "none"}
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z"
            />
          </svg>
          {isFavourite ? 'Saved' : 'Save'}
        </>
      )}
    </button>
  );
}

export default FavouriteButton;